import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { usePermission } from '@/hooks/use-permission';
import { useTheme } from '@/hooks/use-theme';
import { navigationGroups, NavigationGroup, NavigationItem } from '@/config/navigation-optimized';
import { getPageByPath } from '@/config/adminPageRegistry';
import {
  ChevronDown, ChevronRight, LogOut, Search, X, User, Sun, Moon
} from 'lucide-react';

export default function AdminSidebar() {
  const [location, setLocation] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { toast } = useToast();
  const { userRole, canAccessPage } = usePermission();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const permissionFilteredGroups = useMemo(() => {
    if (!userRole) return [];
    return navigationGroups
      .map((group) => {
        const filteredItems = group.items.filter((item) => {
          const page = getPageByPath(item.path);
          if (!page) return true;
          return canAccessPage(page);
        });
        return { ...group, items: filteredItems };
      })
      .filter((group) => group.items.length > 0);
  }, [userRole, canAccessPage]);

  useEffect(() => {
    const initialExpanded = new Set<string>();
    permissionFilteredGroups.forEach(group => {
      if (group.defaultExpanded || group.items.some(item => location === item.path)) {
        initialExpanded.add(group.name);
      }
    });
    setExpandedGroups(initialExpanded);
  }, [permissionFilteredGroups]);

  useEffect(() => {
    permissionFilteredGroups.forEach(group => {
      if (group.items.some(item => location === item.path)) {
        setExpandedGroups(prev => new Set([...prev, group.name]));
      }
    });
  }, [location, permissionFilteredGroups]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(groupName) ? next.delete(groupName) : next.add(groupName);
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      setLocation('/login');
      toast({ title: 'Logged out successfully' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to logout', variant: 'destructive' });
    }
  };

  const filteredGroups = useMemo(() => {
    if (!searchQuery) return permissionFilteredGroups;
    return permissionFilteredGroups.map(group => ({
      ...group,
      items: group.items.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.path.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(group => group.items.length > 0);
  }, [searchQuery, permissionFilteredGroups]);

  const isPathActive = (path: string) => path === location || location.startsWith(path + '/');

  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark');

  const sc = {
    sidebarBg: isDark ? '#0a0f1e' : '#ffffff',
    borderColor: isDark ? '#131c2e' : '#e2e8f0',
    titleColor: isDark ? '#f1f5f9' : '#0f172a',
    subtitleColor: isDark ? '#64748b' : '#94a3b8',
    inputBg: isDark ? '#0f1628' : '#f1f5f9',
    inputBorder: isDark ? '#1e293b' : '#e2e8f0',
    inputText: isDark ? '#e2e8f0' : '#1e293b',
    searchIcon: isDark ? '#475569' : '#94a3b8',
    groupBgActive: isDark ? '#111827' : '#f1f5f9',
    groupIconColor: isDark ? '#64748b' : '#94a3b8',
    groupTextColor: isDark ? '#94a3b8' : '#64748b',
    chevronColor: isDark ? '#475569' : '#cbd5e1',
    itemColor: isDark ? '#94a3b8' : '#64748b',
    userNameColor: isDark ? '#e2e8f0' : '#1e293b',
    userRoleColor: isDark ? '#64748b' : '#94a3b8',
    mobileMenuBg: isDark ? '#0f1628' : '#f1f5f9',
    mobileMenuBorder: isDark ? '#1e293b' : '#e2e8f0',
    mobileMenuText: isDark ? '#e2e8f0' : '#1e293b',
  };

  const sidebarContent = (
    <div style={{
      width: 260, height: '100vh', display: 'flex', flexDirection: 'column',
      background: sc.sidebarBg, borderRight: `1px solid ${sc.borderColor}`,
    }}>
      <div style={{
        height: 60, display: 'flex', alignItems: 'center', padding: '0 16px',
        borderBottom: `1px solid ${sc.borderColor}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/molochain-logo.png" alt="MOLOCHAIN" style={{ height: 28, width: 'auto' }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: sc.titleColor, letterSpacing: '0.05em' }}>MOLOCHAIN</div>
            <div style={{ fontSize: 10, color: sc.subtitleColor, fontWeight: 500 }}>Control Center</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 12px 8px' }}>
        <div style={{ position: 'relative' }}>
          <Search style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            width: 14, height: 14, color: sc.searchIcon,
          }} />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '8px 8px 8px 32px', fontSize: 13,
              background: sc.inputBg, border: `1px solid ${sc.inputBorder}`, borderRadius: 8,
              color: sc.inputText, outline: 'none',
            }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{
              position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            }}>
              <X style={{ width: 12, height: 12, color: sc.subtitleColor }} />
            </button>
          )}
        </div>
      </div>

      <ScrollArea style={{ flex: 1 }}>
        <div style={{ padding: '4px 8px 16px' }}>
          {filteredGroups.map((group) => (
            <div key={group.name} style={{ marginBottom: 4 }}>
              <button
                onClick={() => toggleGroup(group.name)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: expandedGroups.has(group.name) ? sc.groupBgActive : 'transparent',
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {group.icon && <group.icon style={{ width: 15, height: 15, color: sc.groupIconColor }} />}
                  <span style={{ fontSize: 12, fontWeight: 600, color: sc.groupTextColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {group.name}
                  </span>
                </div>
                {expandedGroups.has(group.name)
                  ? <ChevronDown style={{ width: 12, height: 12, color: sc.chevronColor }} />
                  : <ChevronRight style={{ width: 12, height: 12, color: sc.chevronColor }} />
                }
              </button>

              {expandedGroups.has(group.name) && (
                <div style={{ paddingLeft: 4, marginTop: 2 }}>
                  {group.items.map((item) => {
                    const active = isPathActive(item.path);
                    return (
                      <Link key={item.path} href={item.path}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '7px 10px 7px 24px', borderRadius: 6,
                          fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                          background: active ? 'rgba(59,130,246,0.1)' : 'transparent',
                          color: active ? '#60a5fa' : sc.itemColor,
                          fontWeight: active ? 500 : 400,
                          borderLeft: active ? '2px solid #3b82f6' : '2px solid transparent',
                        }}>
                          {item.icon && <item.icon style={{ width: 14, height: 14, opacity: 0.8 }} />}
                          <span style={{ flex: 1 }}>{item.name}</span>
                          {item.badge && (
                            <span style={{
                              fontSize: 10, padding: '1px 6px', borderRadius: 4,
                              background: item.badge === 'NEW' ? '#22c55e20' : '#3b82f620',
                              color: item.badge === 'NEW' ? '#4ade80' : '#60a5fa',
                              fontWeight: 600,
                            }}>
                              {item.badge}
                            </span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      <div style={{ borderTop: `1px solid ${sc.borderColor}`, padding: 12 }}>
        {user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '6px 8px' }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 600, color: '#fff', flexShrink: 0,
            }}>
              {user.username?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: sc.userNameColor, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.username}</p>
              <p style={{ fontSize: 11, color: sc.userRoleColor, margin: 0, textTransform: 'capitalize' }}>{user.role}</p>
            </div>
          </div>
        )}
        <button
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: isDark ? '#111827' : '#f1f5f9',
            color: sc.userNameColor, fontSize: 13,
            transition: 'background 0.15s', marginBottom: 4,
          }}
        >
          {isDark ? (
            <Sun style={{ width: 14, height: 14, color: '#f59e0b' }} />
          ) : (
            <Moon style={{ width: 14, height: 14, color: '#6366f1' }} />
          )}
          {isDark ? 'Light Mode' : 'Dark Mode'}
        </button>
        <button
          onClick={handleLogout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: 'transparent', color: '#ef4444', fontSize: 13,
            transition: 'background 0.15s',
          }}
        >
          <LogOut style={{ width: 14, height: 14 }} />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block">
        {sidebarContent}
      </aside>

      <div className="lg:hidden" style={{ position: 'fixed', top: 12, left: 12, zIndex: 50 }}>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: sc.mobileMenuBg, border: `1px solid ${sc.mobileMenuBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
        >
          {isMobileMenuOpen
            ? <X style={{ width: 18, height: 18, color: sc.mobileMenuText }} />
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ width: 16, height: 2, background: sc.mobileMenuText, borderRadius: 1 }} />
                <span style={{ width: 12, height: 2, background: sc.mobileMenuText, borderRadius: 1 }} />
                <span style={{ width: 16, height: 2, background: sc.mobileMenuText, borderRadius: 1 }} />
              </div>
          }
        </button>
      </div>

      {isMobileMenuOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 40,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}
